import {
  Button, Collapse, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, VStack,
} from "@chakra-ui/react";
import { useState, useEffect, useMemo } from "react";
import { POWER_SOURCES } from "../../../OrganizationQuestionnaire/helpers";

const ELECTRICITY_TYPES = [
  "ELECTRICITY_LOCATION_BASED",
  "ELECTRICITY_MARKET_BASED",
  "ELECTRICITY_ONSITE_GENERATION",
  "RENEWABLE_ENERGY_PROCUREMENT",
  "ELECTRIC_CAR_MEDIUM",
];

const SCOPE_OPTIONS = [
  { value: "scope1", label: "Scope 1" },
  { value: "scope2", label: "Scope 2" },
  { value: "scope3_cat1", label: "Scope 3 – Cat 1: Purchased Goods & Services" },
  { value: "scope3_cat2", label: "Scope 3 – Cat 2: Capital Goods" },
  { value: "scope3_cat4", label: "Scope 3 – Cat 4: Upstream Transportation" },
  { value: "scope3_cat5", label: "Scope 3 – Cat 5: Waste Generated in Operations" },
  { value: "scope3_cat6", label: "Scope 3 – Cat 6: Business Travel" },
  { value: "scope3_cat7", label: "Scope 3 – Cat 7: Employee Commuting" },
  { value: "scope3_cat9", label: "Scope 3 – Cat 9: Downstream Transportation" },
  { value: "scope3_cat11", label: "Scope 3 – Cat 11: Use of Sold Products" },
  { value: "scope3_cat12", label: "Scope 3 – Cat 12: End-of-Life Treatment" },
  { value: "scope3_cat14", label: "Scope 3 – Cat 14: Franchises" },
  { value: "scope3_cat15", label: "Scope 3 – Cat 15: Investments" },
];

const OTHER_UNIT = "__other__";

const FOCUS_STYLE = { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" };

function BespokeFactorModal({ isOpen, onClose, onSave, editData, organizationStructure }) {
  const [scopeCategory, setScopeCategory] = useState("");
  const [fields, setFields] = useState({});
  const [unitSelection, setUnitSelection] = useState("");
  const [customUnit, setCustomUnit] = useState("");

  const availableUnits = useMemo(() => {
    if (!organizationStructure) return [];
    const seen = new Set();
    const units = [];
    const allMeasures = [
      ...(organizationStructure.measures || []).flatMap((m) => m.measures || []),
      ...(organizationStructure.electricityMeasures || []).flatMap((m) => m.measures || []),
    ];
    allMeasures.forEach((m) => {
      if (m?.name && !seen.has(m.name)) {
        seen.add(m.name);
        units.push({ code: m.code, name: m.name });
      }
    });
    units.sort((a, b) => a.name.localeCompare(b.name));
    return units;
  }, [organizationStructure]);

  useEffect(() => {
    if (editData) {
      setScopeCategory(editData.scopeCategory || "");
      setFields(editData.fields || {});
      const unit = editData.fields?.unit || "";
      const isKnownUnit = availableUnits.some((u) => u.name === unit);
      setUnitSelection(isKnownUnit ? unit : unit ? OTHER_UNIT : "");
      setCustomUnit(isKnownUnit ? "" : unit);
    } else {
      setScopeCategory("");
      setFields({});
      setUnitSelection("");
      setCustomUnit("");
    }
  }, [editData, isOpen, availableUnits]);

  const handleFieldChange = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const resolvedUnit = unitSelection === OTHER_UNIT ? customUnit.trim() : unitSelection;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!scopeCategory || !fields.conversionFactor) return;
    const finalFields = { ...fields };
    if (fieldConfig.some((f) => f.key === "unit")) {
      finalFields.unit = resolvedUnit;
    }
    onSave({
      id: editData?.id,
      scopeCategory,
      fields: finalFields,
    });
    onClose();
  };

  const scopeLabel = SCOPE_OPTIONS.find((o) => o.value === scopeCategory)?.label || scopeCategory;

  const fieldConfig = useMemo(() => {
    if (!scopeCategory) return [];

    if (scopeCategory === "scope1") {
      return [
        { key: "name", label: "Consumption Type", required: true },
        { key: "unit", label: "Unit", type: "unit" },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope2") {
      return [
        { key: "name", label: "Electricity Type", type: "electricityType", required: true },
        { key: "unit", label: "Unit", type: "unit" },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope3_cat1" || scopeCategory === "scope3_cat2") {
      return [
        { key: "productCategory", label: "Product Category", required: true },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope3_cat4" || scopeCategory === "scope3_cat9") {
      return [
        { key: "type", label: "Type of Transportation", required: true },
        { key: "subtype", label: "Subtype of Transportation" },
        { key: "subtype2", label: "Subtype 2" },
        { key: "unit", label: "Unit", type: "unit" },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope3_cat5" || scopeCategory === "scope3_cat12") {
      return [
        { key: "wasteType", label: "Waste Type", required: true },
        { key: "disposalMethod", label: "Disposal Method" },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope3_cat6" || scopeCategory === "scope3_cat7") {
      return [
        { key: "modeOfTransport", label: "Mode of Transport", required: true },
        { key: "transportSubtype", label: "Transport Subtype" },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
        { key: "source", label: "Source" },
      ];
    }
    if (scopeCategory === "scope3_cat11") {
      return [
        { key: "type", label: "Type", required: true },
        { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
      ];
    }
    return [
      { key: "name", label: "Name", required: true },
      { key: "unit", label: "Unit", type: "unit" },
      { key: "conversionFactor", label: "Conversion Factor", type: "number", required: true },
      { key: "source", label: "Source" },
    ];
  }, [scopeCategory]);

  const isValid = scopeCategory && fields.conversionFactor && fieldConfig.filter((f) => f.required).every((f) => f.key === "conversionFactor" || fields[f.key]?.toString().trim());

  const renderField = (config) => {
    if (config.type === "electricityType") {
      return (
        <FormControl key={config.key} isRequired={config.required}>
          <FormLabel fontSize="sm">{config.label}</FormLabel>
          <Select
            size="sm"
            borderRadius="md"
            value={fields[config.key] || ""}
            onChange={(e) => handleFieldChange(config.key, e.target.value)}
            placeholder="Select electricity type"
            _focus={FOCUS_STYLE}
          >
            {ELECTRICITY_TYPES.map((et) => (
              <option key={et} value={et}>{POWER_SOURCES[et] || et}</option>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (config.type === "unit") {
      return (
        <FormControl key={config.key} isRequired={config.required}>
          <FormLabel fontSize="sm">{config.label}</FormLabel>
          <Select
            size="sm"
            borderRadius="md"
            value={unitSelection}
            onChange={(e) => { setUnitSelection(e.target.value); if (e.target.value !== OTHER_UNIT) setCustomUnit(""); }}
            placeholder="Select unit"
            _focus={FOCUS_STYLE}
          >
            {availableUnits.map((u) => (
              <option key={u.code} value={u.name}>{u.name}</option>
            ))}
            <option value={OTHER_UNIT}>Other...</option>
          </Select>
          <Collapse in={unitSelection === OTHER_UNIT} animateOpacity>
            <Input size="sm" borderRadius="md" mt={2} value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} placeholder="Enter custom unit" _focus={FOCUS_STYLE} />
          </Collapse>
        </FormControl>
      );
    }
    return (
      <FormControl key={config.key} isRequired={config.required}>
        <FormLabel fontSize="sm">{config.label}</FormLabel>
        <Input
          size="sm"
          borderRadius="md"
          type={config.type === "number" ? "number" : "text"}
          step={config.type === "number" ? "any" : undefined}
          value={fields[config.key] || ""}
          onChange={(e) => handleFieldChange(config.key, config.type === "number" ? e.target.value : e.target.value)}
          placeholder={config.label}
          _focus={FOCUS_STYLE}
        />
      </FormControl>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit}>
        <ModalHeader>{editData ? "Edit Custom Factor" : "Add Custom Factor"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Scope / Category</FormLabel>
            <Select
              size="sm"
              borderRadius="md"
              value={scopeCategory}
              onChange={(e) => { setScopeCategory(e.target.value); setFields({}); setUnitSelection(""); setCustomUnit(""); }}
              placeholder="Select scope or category"
              _focus={FOCUS_STYLE}
            >
              <optgroup label="Direct & Indirect">
                <option value="scope1">Scope 1 – Direct Emissions</option>
                <option value="scope2">Scope 2 – Indirect Emissions</option>
              </optgroup>
              <optgroup label="Scope 3 Categories">
                {SCOPE_OPTIONS.filter((o) => o.value.startsWith("scope3_")).map((o) => (
                  <option key={o.value} value={o.value}>{o.label.replace("Scope 3 – ", "")}</option>
                ))}
              </optgroup>
            </Select>
          </FormControl>

          {scopeCategory && (
            <VStack spacing={4} align="stretch">
              {fieldConfig.map(renderField)}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" isDisabled={!isValid}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BespokeFactorModal;
